/**
 * 챌린지 선택 팝업 — 검색 · 필터 · 등록일 · 참여자 · 내 기록 · 좋아요(2026-10-07, 첨부 기획서 1부).
 *
 * 목록은 **서버가 보이는 것만** 준다(QBase challengeSummary — 작성자·LSADMIN·전체·그룹 구성원·지정 사용자). 수가 적어
 * 검색·필터·정렬은 화면이 한다(QBase 세션과 정한 것). 종류(쿼리/튜닝)는 부르는 쪽이 이미 거른 후보(`candidates`)로 맞춘다 —
 * 집계 목록에는 문항 유형이 없어 튜닝 판정은 QueryChallenge 의 것을 그대로 쓴다.
 * 좋아요는 클라우드에서만(로컬 「가져온 것」 은 혼자 쓰는 곳이라 없다), 자기 챌린지에는 누를 수 없다(서버도 400).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiSearch, FiHeart, FiList } from 'react-icons/fi';
import AppModal from '../common/AppModal';
import { listChallengeSummary, toggleChallengeLike } from '../../services/livesqlRepo';
import { dbmsOf, isDraftChallenge } from '../../utils/challengeAttr';
import { formatApiError } from '../../utils/formatters';
import { filterSummaries, SORTS } from '../../utils/challengePicker';
import './ChallengePickerModal.css';

const LEVEL_LABEL = { '01': 'Beginner', '02': 'Intermediate', '03': 'Expert' };
/** 14자리(YYYYMMDDhhmmss) → YYYY-MM-DD */
const day = (v) => { const m = /^(\d{4})(\d{2})(\d{2})/.exec(String(v || '')); return m ? `${m[1]}-${m[2]}-${m[3]}` : ''; };

function ChallengePickerModal({ open, onClose, onPick, candidates, isTuning, cloud, currentUsrid, selectedId }) {
    const { t } = useTranslation();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');
    const [q, setQ] = useState('');
    const [levels, setLevels] = useState(['01', '02', '03']);
    const [dbms, setDbms] = useState('');
    const [scope, setScope] = useState('');
    const [onlyMine, setOnlyMine] = useState(false);
    const [onlyLiked, setOnlyLiked] = useState(false);
    const [sort, setSort] = useState('recent');
    const [liking, setLiking] = useState('');
    const tRef = useRef(t);
    tRef.current = t;

    useEffect(() => {
        if (!open) return undefined;
        let alive = true;
        setRows(null); setError('');
        listChallengeSummary()
            .then((r) => { if (alive) setRows(r); })
            .catch((e) => { if (alive) { setRows([]); setError(formatApiError(e, tRef.current)); } });
        return () => { alive = false; };
    }, [open]);

    // 후보(종류가 맞는 것)와 집계를 잇는다 — 집계에 없는 후보는 집계 없이(로컬·집계 실패), 후보에 없는 집계 행은 다른 종류라 뺀다
    const merged = useMemo(() => {
        const byId = new Map((rows || []).map(r => [String(r.qryChallengeId), r]));
        return (candidates || []).map((ch) => {
            const id = String(ch.QRY_CHALLENGE_ID);
            const r = byId.get(id) || {};
            return {
                id,
                name: r.challengeNm || ch.CHALLENGE_NM || '',
                level: String(r.challengeLevelDcd || ch.CHALLENGE_LEVEL_DCD || ''),
                dbms: dbmsOf(ch),
                draft: isDraftChallenge(ch),
                scope: r.openScopeCd || (ch.PUBLIC_YN === 'Y' ? 'ALL' : 'PRIVATE'),
                regDt: r.regDt || '',
                author: r.mgrUserKnme || '',
                mine: !!currentUsrid && String(ch.MGR_USRID || r.mgrUsrid || '') === String(currentUsrid),
                quizCnt: r.quizCnt || 0,
                ptcpUserCnt: r.ptcpUserCnt || 0,
                likeCnt: r.likeCnt || 0,
                myLikeYn: r.myLikeYn || 'N',
                myQuizCnt: r.myQuizCnt || 0,
                myCorrectCnt: r.myCorrectCnt || 0,
                myLastSbmsnDt: r.myLastSbmsnDt || '',
                hasSummary: !!byId.get(id),
            };
        });
    }, [rows, candidates, currentUsrid]);

    const shown = useMemo(
        () => filterSummaries(merged, { q, levels, dbms: isTuning ? dbms : '', scope, onlyMine, onlyLiked, sort }),
        [merged, q, levels, dbms, isTuning, scope, onlyMine, onlyLiked, sort],
    );

    const like = useCallback(async (e, row) => {
        e.stopPropagation();
        setLiking(row.id);
        try {
            const r = await toggleChallengeLike(row.id);
            setRows(prev => (prev || []).map(x => (String(x.qryChallengeId) === row.id ? { ...x, myLikeYn: r.myLikeYn, likeCnt: r.likeCnt } : x)));
        } catch (err) {
            setError(formatApiError(err, t));
        } finally {
            setLiking('');
        }
    }, [t]);

    const pick = (row) => { onPick(row.id); onClose(); };

    return (
        <AppModal open={open} onClose={onClose} title={t('challenge:picker.title')} icon={<FiList size={14} />} className="app-modal-default cpk-modal">
            <div className="cpk-body">
                <div className="cpk-filters">
                    <label className="cpk-search">
                        <FiSearch size={13} />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('challenge:picker.search')} autoFocus />
                    </label>
                    <div className="cpk-chips">
                        {['01', '02', '03'].map(c => (
                            <label key={c} className="cpk-chip">
                                <input type="checkbox" checked={levels.includes(c)}
                                    onChange={() => setLevels(p => (p.includes(c) ? (p.length === 1 ? p : p.filter(x => x !== c)) : [...p, c]))} />
                                {LEVEL_LABEL[c]}
                            </label>
                        ))}
                    </div>
                    {isTuning ? (
                        <select value={dbms} onChange={(e) => setDbms(e.target.value)} aria-label={t('challenge:dbms.filterLabel')}>
                            <option value="">{t('challenge:dbms.all')}</option>
                            <option value="oracle">{t('challenge:dbms.oracle')}</option>
                            <option value="postgresql">{t('challenge:dbms.postgresql')}</option>
                        </select>
                    ) : null}
                    {cloud ? (
                        <select value={scope} onChange={(e) => setScope(e.target.value)} aria-label={t('challenge:scope.label')}>
                            <option value="">{t('challenge:picker.anyScope')}</option>
                            {['ALL', 'GROUP', 'USER', 'PRIVATE'].map(c => <option key={c} value={c}>{t(`challenge:scope.codes.${c}`)}</option>)}
                        </select>
                    ) : null}
                    <label className="cpk-chip"><input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />{t('challenge:picker.onlyMine')}</label>
                    {cloud ? (
                        <label className="cpk-chip"><input type="checkbox" checked={onlyLiked} onChange={(e) => setOnlyLiked(e.target.checked)} />{t('challenge:picker.onlyLiked')}</label>
                    ) : null}
                    <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label={t('challenge:picker.sort')} className="cpk-sort">
                        {SORTS.map(s => <option key={s} value={s}>{t(`challenge:picker.sorts.${s}`)}</option>)}
                    </select>
                </div>
                {error ? <div className="cpk-error">{error}</div> : null}
                <div className="cpk-count">
                    {rows === null ? t('challenge:picker.loading') : t('challenge:picker.count', { n: shown.length, total: merged.length })}
                </div>
                <div className="cpk-tableWrap">
                    <table className="cpk-table">
                        <thead>
                            <tr>
                                <th>{t('challenge:picker.col.name')}</th>
                                <th>{t('challenge:picker.col.level')}</th>
                                <th className="cpk-num">{t('challenge:picker.col.quizzes')}</th>
                                <th>{t('challenge:picker.col.regDt')}</th>
                                <th>{t('challenge:picker.col.author')}</th>
                                <th className="cpk-num">{t('challenge:picker.col.participants')}</th>
                                <th>{t('challenge:picker.col.mine')}</th>
                                {cloud ? <th className="cpk-num">{t('challenge:picker.col.likes')}</th> : null}
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map(row => (
                                <tr key={row.id} className={row.id === String(selectedId) ? 'cpk-on' : ''} onClick={() => pick(row)}
                                    tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') pick(row); }}>
                                    <td>
                                        <span className="cpk-name">{isTuning ? `[${t(`challenge:dbms.${row.dbms}`)}] ` : ''}{row.name}</span>
                                        {row.draft ? <span className="cpk-badge cpk-draft">{t('challenge:edit.statuses.draft')}</span> : null}
                                        {cloud && row.scope !== 'ALL' ? <span className="cpk-badge">{t(`challenge:scope.codes.${row.scope}`)}</span> : null}
                                    </td>
                                    <td>{LEVEL_LABEL[row.level] || '-'}</td>
                                    <td className="cpk-num">{row.quizCnt || '-'}</td>
                                    <td>{day(row.regDt) || '-'}</td>
                                    <td>{row.author || '-'}</td>
                                    <td className="cpk-num">{row.hasSummary ? row.ptcpUserCnt : '-'}</td>
                                    <td className="cpk-mine">
                                        {row.myQuizCnt
                                            ? t('challenge:picker.myRecord', { done: row.myQuizCnt, total: row.quizCnt || '?', correct: row.myCorrectCnt, at: day(row.myLastSbmsnDt) || '-' })
                                            : <span className="cpk-dim">{t('challenge:picker.notTried')}</span>}
                                    </td>
                                    {cloud ? (
                                        <td className="cpk-num">
                                            <button
                                                type="button" className={`cpk-like${row.myLikeYn === 'Y' ? ' cpk-liked' : ''}`}
                                                disabled={row.mine || liking === row.id || !row.hasSummary}
                                                title={row.mine ? t('challenge:picker.ownLike') : t('challenge:picker.like')}
                                                aria-pressed={row.myLikeYn === 'Y'}
                                                onClick={(e) => like(e, row)}
                                            >
                                                <FiHeart size={12} /> {row.likeCnt}
                                            </button>
                                        </td>
                                    ) : null}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {rows !== null && !shown.length ? <div className="cpk-empty">{t('challenge:picker.empty')}</div> : null}
                </div>
            </div>
        </AppModal>
    );
}

ChallengePickerModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onPick: PropTypes.func.isRequired,
    candidates: PropTypes.arrayOf(PropTypes.object).isRequired,
    isTuning: PropTypes.bool,
    cloud: PropTypes.bool,
    currentUsrid: PropTypes.string,
    selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
ChallengePickerModal.defaultProps = { open: false, isTuning: false, cloud: true, currentUsrid: '', selectedId: '' };

export default ChallengePickerModal;
